/**
 * Generic CRM Object detail page - full-page view for any CRM record type.
 *
 * This component handles contacts, companies, and deals through a single
 * generic implementation driven by the entity type. For custom entities
 * (org_* types), the entity type is resolved at runtime from the URL slug.
 */

import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from '@untitledui/icons';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useParams } from 'react-router-dom';

import { useUser } from '@/allauth_auth/hooks';
import { useOrg } from '@/contexts/OrgContext';
import { QueryErrorState } from '@/lib/components';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import type { EntityTypeName } from '@/lib/crm/core/types';
import { CRMDetailSkeleton } from '@/lib/crm/record/components/CRMDetailSkeleton';
import { CRMObjectFullPageView } from '@/lib/crm/record/components/CRMObjectFullPageView';
import { useCRMObject } from '@/lib/crm/record/hooks/useCRMObject';
import { getStoredRailWidth } from '@/lib/crm/record/hooks/useRailWidth';
import { useBuildCustomEntityConfig } from '@/lib/crm/record/registry/buildCustomEntityConfig';
import {
  resolveEntityId,
  subscribeToStoredObject,
  usePendingCreation404Recovery,
} from '@/lib/crm/store';
import { toSnakeCase } from '@/lib/crm/utils/entityTypeUtils';
import {
  useViewHealthErrorMarker,
  useViewHealthMarker,
} from '@/lib/view-health';

interface CRMObjectDetailPageProps {
  entityType?: EntityTypeName;
}

export default function CRMObjectDetailPage({
  entityType: entityTypeProp,
}: CRMObjectDetailPageProps) {
  const { recordId, entitySlug } = useParams<{
    recordId: string;
    entitySlug?: string;
  }>();
  const { currentOrg, isLoading: orgLoading } = useOrg();
  const user = useUser();

  const orgId = currentOrg?.id;

  const needsSlugResolution = !entityTypeProp && Boolean(entitySlug);

  const objectTypesQuery = useQuery({
    queryKey: ['objectTypes', orgId, 'resolveSlug', entitySlug],
    queryFn: async (): Promise<string | null> => {
      if (!orgId || !entitySlug) {
        return null;
      }
      const resolvedName = toSnakeCase(entitySlug);
      // This generic full-page route is only for custom (org_*) entity types;
      // system entities reach their full-page view via hardcoded routes with
      // an explicit entityType prop. Also reject `*_timeline` aliases — they
      // are timeline-view URLs, not real entity-type identifiers.
      if (
        !resolvedName.startsWith('org_') ||
        resolvedName.endsWith('_timeline')
      ) {
        return null;
      }
      const client = createSchemaClient(orgId);
      const objectTypes = await client.getObjectTypes();
      const match = objectTypes.find(t => t.entity_type_name === resolvedName);
      return match?.entity_type_name ?? null;
    },
    enabled: needsSlugResolution && Boolean(orgId),
  });

  const resolvedEntityType = objectTypesQuery.data ?? undefined;

  const effectiveEntityType: EntityTypeName | undefined =
    entityTypeProp ?? (resolvedEntityType as EntityTypeName | undefined);

  const { object, loading, error, errorStatusCode, refetch } = useCRMObject(
    orgId,
    (effectiveEntityType ?? 'ahoy_contact') as EntityTypeName,
    recordId,
    user?.id ?? 0,
    { enabled: Boolean(effectiveEntityType) }
  );

  // A 404 for a pending optimistic creation is not authoritative — the server
  // just hasn't projected the entity the user created moments ago. Render the
  // store copy instead of an error page; the hook refetches once pending
  // resolves, so the view converges on the record (confirmed) or an honest
  // error (orphaned). See usePendingCreation404Recovery.
  const isPendingCreation = usePendingCreation404Recovery({
    entityId: recordId,
    statusCode: errorStatusCode,
    refetch,
  });

  // `object` above is sourced from useCRMObject, which is gated on
  // `meta.isComplete` — a freshly optimistic-created record stays incomplete
  // until the server confirms it, i.e. for the entire pending-creation 404
  // window. So `object` is always undefined here regardless of `isPendingCreation`.
  // Read the store entry directly (completeness-independent, same surface
  // single-slideout.tsx uses) to get a renderable copy of the record during
  // that window.
  const resolvedRecordId = recordId ? resolveEntityId(recordId) : '';
  const {
    subscribe: storedObjectSubscribe,
    getSnapshot: storedObjectGetSnapshot,
  } = useMemo(
    () => subscribeToStoredObject(resolvedRecordId),
    [resolvedRecordId]
  );
  const storedObject = useSyncExternalStore(
    storedObjectSubscribe,
    storedObjectGetSnapshot
  );
  const pendingCreationObject =
    errorStatusCode === 404 && isPendingCreation
      ? storedObject?.data
      : undefined;
  const suppress404 =
    errorStatusCode === 404 &&
    isPendingCreation &&
    Boolean(pendingCreationObject);
  const displayObject = object ?? pendingCreationObject;

  const {
    config,
    isLoading: configLoading,
    error: configError,
  } = useBuildCustomEntityConfig(orgId, effectiveEntityType);

  useViewHealthMarker('crm-entity-detail-ready', {
    ready:
      !orgLoading &&
      !(needsSlugResolution && objectTypesQuery.isLoading) &&
      !configLoading &&
      !loading &&
      !configError &&
      !!object,
  });
  useViewHealthErrorMarker('crm-entity-detail-schema-failed', {
    triggered:
      (needsSlugResolution && objectTypesQuery.isError) || !!configError,
  });

  const handleReload = useCallback(() => window.location.reload(), []);

  const renderErrorState = (err: unknown) => (
    <div className='p-6' role='alert'>
      <QueryErrorState
        error={err}
        label={`Unable to load ${config.displayName.toLowerCase()}`}
        onRetry={handleReload}
      />
    </div>
  );

  if (
    orgLoading ||
    (needsSlugResolution && objectTypesQuery.isLoading) ||
    configLoading ||
    loading
  ) {
    return <CRMDetailSkeleton railWidth={getStoredRailWidth()} />;
  }

  if (needsSlugResolution && objectTypesQuery.isError) {
    return renderErrorState(objectTypesQuery.error);
  }

  if (configError) {
    return renderErrorState(configError);
  }

  if (
    needsSlugResolution &&
    objectTypesQuery.isSuccess &&
    !resolvedEntityType
  ) {
    return (
      <div className='flex items-center justify-center min-h-dvh'>
        <div className='text-center'>
          <h1 className='text-heading-2 mb-2 text-[var(--color-text-primary)]'>
            Unsupported record type
          </h1>
          <p className='text-body text-[var(--color-text-secondary)]'>
            This record type is not configured for full-page view.
          </p>
        </div>
      </div>
    );
  }

  if (error && !suppress404) {
    return renderErrorState(error);
  }

  if (!displayObject) {
    return (
      <div className='p-6'>
        <div className='p-4 bg-warning-50 border border-warning-200 rounded-xl'>
          <div className='flex items-center gap-3'>
            <AlertTriangle className='w-5 h-5 text-warning-600' />
            <p className='text-warning-900 font-medium'>Record not found</p>
          </div>
        </div>
      </div>
    );
  }

  // Keyed on entity_id so navigating between records (e.g. via search or a
  // related-entity link) always tears down and recreates the view rather than
  // reusing the mounted instance — otherwise its `activityFilters` state
  // (seeded once via a mount-only useState initializer) would carry over from
  // the previous record instead of reinitializing from the new entity type's
  // registry defaults.
  return (
    <CRMObjectFullPageView
      key={displayObject.entity_id}
      object={displayObject}
      config={config}
    />
  );
}
